import Cookies from 'js-cookie'
import { isIE } from 'react-device-detect'
import {
  tenMinutes,
  fiveYears,
  oneDay,
  oneDayMinutes,
} from 'scripts/jsCookieExpireMinutes'
import { FingerPrintHash } from 'types/FingerPrint'
import { env } from './env'
import { customBaseDomainFromHost } from 'scripts/customBaseDomain'

const get = (name: string) => {
  return Cookies.get(name)
}

// ie not allow secure
const set = (
  name: string,
  value: string,
  options?: Cookies.CookieAttributes,
) => {
  if (options?.secure && isIE) {
    delete options?.secure
  }
  Cookies.set(name, value, options)
}

const remove = (name: string, options?: Cookies.CookieAttributes) => {
  if (options?.secure && isIE) {
    delete options?.secure
  }
  Cookies.remove(name, options)
}

const secure = env.COOKIE_SECURE

// OEM / ホワイトラベル: cookie domain を runtime で決める。
// - LOCKED ドメイン → env.COOKIE_DOMAIN（.auth.locked.jp）を維持（無回帰）。
// - custom domain `{tenant}.auth.{base}` → `.auth.{base}`（パートナードメイン単位で独立）。
// 機密 cookie（session_token / _locked_hash / _locked_device_uuid）の domain。
// custom domain では undefined（host-only）にして partner 配下の別 tenant に共有させない。
const sensitiveCookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') return env.COOKIE_DOMAIN
  const base = customBaseDomainFromHost(window.location.hostname)
  return base ? undefined : env.COOKIE_DOMAIN
}

// 非機密の「前回テナント」redirect 補助（_locked_auth_subdomain）のみ partner ドメイン単位で
// 共有してよいので custom domain では `.auth.{base}` を使う。
const sharedRedirectCookieDomain = (): string => {
  if (typeof window === 'undefined') return env.COOKIE_DOMAIN
  const base = customBaseDomainFromHost(window.location.hostname)
  return base ? `.auth.${base}` : env.COOKIE_DOMAIN
}

const lockedDeviceUuidOptions = {
  expires: fiveYears,
  secure,
  domain: sensitiveCookieDomain(),
}

const lockedAuthSubdomainOptions = {
  expires: fiveYears,
  secure,
  domain: sharedRedirectCookieDomain(),
}

const generateSessionTokenExpires = (sessionTokenExpiredAtMinutes: number) => {
  return oneDay / (oneDayMinutes / sessionTokenExpiredAtMinutes)
}

const setDeviceUuidToCookie = (uuid: string) => {
  set('_locked_device_uuid', uuid, lockedDeviceUuidOptions)
}

const setLockedEventIdToCookie = (eventId: string) => {
  set('_locked_event_id', eventId, {
    expires: tenMinutes,
    secure,
  })
}

const setLockedHashToCookie = (fingerprintHash: FingerPrintHash) => {
  const hash = get('_locked_hash')
  if (!hash) {
    set('_locked_hash', fingerprintHash as string, {
      expires: fiveYears,
      secure,
      domain: sensitiveCookieDomain(),
    })
  }
}

const setSessionTokenToCookie = (sessionToken: string, expires: number) => {
  set('session_token', sessionToken, {
    domain: sensitiveCookieDomain(),
    expires,
    secure,
  })
}

const setAuthSubdomainToCookie = (subdomain: string) => {
  set('_locked_auth_subdomain', subdomain, lockedAuthSubdomainOptions)
}

const removeSessionToken = () => {
  remove('session_token', { domain: sensitiveCookieDomain() })
}

const removeLockedEventId = () => {
  remove('_locked_event_id')
}

export const cookies = {
  get,
  setDeviceUuidToCookie,
  setLockedEventIdToCookie,
  setLockedHashToCookie,
  setSessionTokenToCookie,
  generateSessionTokenExpires,
  removeSessionToken,
  removeLockedEventId,
  setAuthSubdomainToCookie,
}
